import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://gamrent.gm";
const SITE_NAME = "Gamrent";
const DEFAULT_DESCRIPTION =
  "Gamrent is a property and rental management platform for owners and managers in The Gambia. Manage properties, tenants, rent invoices, payments, maintenance and reports in one dashboard.";

const PUBLIC_PAGES = {
  "/": {
    title: "Gamrent | Property Management Platform",
    description: DEFAULT_DESCRIPTION,
  },
  "/features": {
    title: "Features | Gamrent",
    description:
      "Portfolio management, tenants and leases, invoicing and payments, maintenance, reports and role-based access. Everything needed to run rental operations.",
  },
  "/how-it-works": {
    title: "How It Works | Gamrent",
    description:
      "Create your company account, share payment evidence with Gamrent admin and get your dashboard activated. A clear journey from signup to daily operations.",
  },
  "/pricing": {
    title: "Pricing | Gamrent",
    description: "Simple monthly and yearly plans in GMD for every portfolio size. Compare Gamrent plans and choose the one that fits.",
  },
  "/contact": {
    title: "Contact | Gamrent",
    description: "Get in touch with Gamrent by email, phone or WhatsApp for product questions, onboarding help or enterprise support.",
  },
  "/signup": {
    title: "Create Account | Gamrent",
    description: "Create your Gamrent company account and start managing your properties, tenants and payments.",
  },
  "/login": {
    title: "Sign In | Gamrent",
    description: "Sign in to your Gamrent dashboard.",
  },
  "/forgot-password": {
    title: "Forgot Password | Gamrent",
    description: "Reset your Gamrent account password.",
  },
};

const APP_TITLES = {
  "/dashboard": "Dashboard",
  "/superadmin": "Super Admin",
  "/superadmin/companies": "Companies",
  "/superadmin/subscriptions": "Subscriptions",
  "/superadmin/settings": "System Settings",
  "/superadmin/company-users": "Company Users",
  "/assets": "Properties",
  "/asset-owners": "Property Owners",
  "/tenants": "Tenants",
  "/rentals": "Rentals",
  "/payments": "Payments",
  "/maintenance": "Maintenance",
  "/users": "Users",
  "/profile": "Profile",
  "/company-settings": "Company Settings",
  "/imports": "Import & Export",
  "/notifications": "Notifications",
  "/messages": "Messages",
  "/reports": "Reports",
  "/invoice": "Invoice",
  "/reset-password": "Reset Password",
};

const setMeta = (attr, key, content) => {
  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

const setCanonical = (href) => {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
};

/** Keeps the tab title, description, social tags and robots directive in sync with the current route. */
const RouteMeta = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
    const page = PUBLIC_PAGES[path];

    if (page) {
      const url = `${SITE_URL}${path === "/" ? "/" : path}`;
      document.title = page.title;
      setMeta("name", "description", page.description);
      setMeta("name", "robots", "index, follow");
      setMeta("property", "og:title", page.title);
      setMeta("property", "og:description", page.description);
      setMeta("property", "og:url", url);
      setMeta("name", "twitter:title", page.title);
      setMeta("name", "twitter:description", page.description);
      setCanonical(url);
      return;
    }

    const appTitle = APP_TITLES[path];
    document.title = appTitle ? `${appTitle} | ${SITE_NAME}` : SITE_NAME;
    setMeta("name", "robots", "noindex, nofollow");
  }, [pathname]);

  return null;
};

export default RouteMeta;
